import { router, useForm, usePage } from "@inertiajs/react"
import type { ReactNode } from "react"

import { AdminLayout } from "@/layouts/AdminLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import type { SharedProps } from "@/types"

interface PropertySettings {
  id: number
  name: string
  address: string | null
  contact_email: string | null
  contact_phone: string | null
  property_type: string
  slug: string
  branding: { primary_color?: string; welcome_message?: string }
  cover_image_url: string | null
  logo_image_url: string | null
  wifi_config: Record<string, string | boolean>
}

interface Props {
  property: PropertySettings
  update_url: string
}

const WIFI_INTEGRATION_LABELS: Record<string, string> = {
  post_login_redirect: "Post-login redirect (most common)",
  captive_portal:      "Captive portal (iframe/hosted)",
  qr_only:             "QR only — no WiFi redirect",
}

function GeneralTab({ property, update_url }: { property: PropertySettings; update_url: string }) {
  const { errors } = usePage<SharedProps>().props
  const { data, setData, patch, processing } = useForm({
    property: {
      name:          property.name,
      address:       property.address ?? "",
      contact_email: property.contact_email ?? "",
      contact_phone: property.contact_phone ?? "",
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(update_url)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-lg">
      <div className="space-y-1.5">
        <Label htmlFor="name">Property name</Label>
        <Input
          id="name"
          value={data.property.name}
          onChange={e => setData("property", { ...data.property, name: e.target.value })}
        />
        {errors["property.name"] && (
          <p className="text-xs text-destructive">{errors["property.name"]}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="address">Address</Label>
        <Textarea
          id="address"
          rows={2}
          value={data.property.address}
          onChange={e => setData("property", { ...data.property, address: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact_email">Contact email</Label>
        <Input
          id="contact_email"
          type="email"
          value={data.property.contact_email}
          onChange={e => setData("property", { ...data.property, contact_email: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact_phone">Contact phone</Label>
        <Input
          id="contact_phone"
          type="tel"
          value={data.property.contact_phone}
          onChange={e => setData("property", { ...data.property, contact_phone: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Slug (read-only)</Label>
        <Input value={property.slug} readOnly className="bg-muted text-muted-foreground" />
        <p className="text-xs text-muted-foreground">
          Slug changes affect the WiFi captive portal URL. Contact an admin to change it.
        </p>
      </div>

      <Button type="submit" disabled={processing}>
        {processing ? "Saving…" : "Save general settings"}
      </Button>
    </form>
  )
}

function BrandingTab({ property, update_url }: { property: PropertySettings; update_url: string }) {
  const { errors } = usePage<SharedProps>().props
  const { data, setData, patch, processing } = useForm({
    property: {
      branding: {
        primary_color:   property.branding.primary_color   ?? "",
        welcome_message: property.branding.welcome_message ?? "",
      },
      cover_image: null as File | null,
      logo_image:  null as File | null,
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(update_url, { forceFormData: true })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-lg">
      <div className="space-y-1.5">
        <Label htmlFor="primary_color">Primary color</Label>
        <div className="flex gap-2">
          <Input
            id="primary_color"
            type="color"
            className="h-10 w-14 cursor-pointer p-1"
            value={data.property.branding.primary_color || "#000000"}
            onChange={e => setData("property", {
              ...data.property,
              branding: { ...data.property.branding, primary_color: e.target.value },
            })}
          />
          <Input
            value={data.property.branding.primary_color}
            placeholder="#000000"
            onChange={e => setData("property", {
              ...data.property,
              branding: { ...data.property.branding, primary_color: e.target.value },
            })}
          />
        </div>
        {errors["property.branding.primary_color"] && (
          <p className="text-xs text-destructive">{errors["property.branding.primary_color"]}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="welcome_message">Welcome message</Label>
        <Textarea
          id="welcome_message"
          rows={3}
          value={data.property.branding.welcome_message}
          placeholder="Your personalised guide to local restaurants, activities, and tips is ready."
          onChange={e => setData("property", {
            ...data.property,
            branding: { ...data.property.branding, welcome_message: e.target.value },
          })}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="cover_image">Cover photo</Label>
        {property.cover_image_url && (
          <img
            src={property.cover_image_url}
            alt="Current cover"
            className="h-32 w-full rounded-lg object-cover"
          />
        )}
        <Input
          id="cover_image"
          type="file"
          accept="image/*"
          onChange={e => setData("property", {
            ...data.property,
            cover_image: e.target.files?.[0] ?? null,
          })}
        />
        <p className="text-xs text-muted-foreground">
          Shown on the WiFi captive portal. Recommended: 1260×750px.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="logo_image">Logo</Label>
        {property.logo_image_url && (
          <img
            src={property.logo_image_url}
            alt="Current logo"
            className="h-16 rounded-lg object-contain"
          />
        )}
        <Input
          id="logo_image"
          type="file"
          accept="image/*"
          onChange={e => setData("property", {
            ...data.property,
            logo_image: e.target.files?.[0] ?? null,
          })}
        />
      </div>

      <Button type="submit" disabled={processing}>
        {processing ? "Saving…" : "Save branding"}
      </Button>
    </form>
  )
}

function WifiTab({ property, update_url }: { property: PropertySettings; update_url: string }) {
  const { errors } = usePage<SharedProps>().props
  const config = property.wifi_config
  const redirectUrl = `${window.location.origin}/${property.slug}`

  const { data, setData, patch, processing } = useForm({
    property: {
      wifi_config: {
        integration_type: (config.integration_type as string) ?? "qr_only",
        is_configured:    config.is_configured ? "true" : "false",
      },
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch(update_url)
  }

  function copyUrl() {
    navigator.clipboard.writeText(redirectUrl).catch(() => {})
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-lg">
      <div className="space-y-1.5">
        <Label>Your WiFi redirect URL</Label>
        <div className="flex gap-2">
          <Input value={redirectUrl} readOnly className="bg-muted text-muted-foreground font-mono text-sm" />
          <Button type="button" variant="outline" onClick={copyUrl}>
            Copy
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Paste this URL in your router or captive portal admin panel.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="integration_type">WiFi integration type</Label>
        <Select
          value={data.property.wifi_config.integration_type}
          onValueChange={v => setData("property", {
            ...data.property,
            wifi_config: { ...data.property.wifi_config, integration_type: v },
          })}
        >
          <SelectTrigger id="integration_type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(WIFI_INTEGRATION_LABELS).map(([value, label]) => (
              <SelectItem key={value} value={value}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors["property.wifi_config.integration_type"] && (
          <p className="text-xs text-destructive">{errors["property.wifi_config.integration_type"]}</p>
        )}
      </div>

      <div className="flex items-center gap-3">
        <input
          id="is_configured"
          type="checkbox"
          checked={data.property.wifi_config.is_configured === "true"}
          onChange={e => setData("property", {
            ...data.property,
            wifi_config: { ...data.property.wifi_config, is_configured: e.target.checked ? "true" : "false" },
          })}
          className="h-4 w-4 rounded border"
        />
        <Label htmlFor="is_configured">Mark WiFi as configured</Label>
      </div>

      <Button type="submit" disabled={processing}>
        {processing ? "Saving…" : "Save WiFi settings"}
      </Button>
    </form>
  )
}

export default function Settings({ property, update_url }: Props) {
  const searchParams = new URLSearchParams(
    typeof window !== "undefined" ? window.location.search : ""
  )
  const defaultTab = searchParams.get("tab") ?? "general"

  function handleTabChange(tab: string) {
    router.get(
      update_url,
      { tab },
      { preserveState: true, replace: true }
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div>
        <h1 className="text-xl font-semibold">{property.name}</h1>
        <p className="text-sm text-muted-foreground">Property settings</p>
      </div>

      <Tabs defaultValue={defaultTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="wifi">WiFi Setup</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="pt-4">
          <GeneralTab property={property} update_url={update_url} />
        </TabsContent>
        <TabsContent value="branding" className="pt-4">
          <BrandingTab property={property} update_url={update_url} />
        </TabsContent>
        <TabsContent value="wifi" className="pt-4">
          <WifiTab property={property} update_url={update_url} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

Settings.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
